import { useSyncExternalStore } from 'react';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '@cfa/react-core';

import { toDataColorMode } from '@/hooks/useColorMode';
import { useFlags } from '@/hooks/useFlags';
import { useBreakpointUp } from '@/hooks/useMediaQuery';

import { userPrefsAtom } from '@/atoms/userPrefs';
import congratsAnimationUrl from '@/icons/CongratsAnimation/congrats-animation.json?url';

const subscribePrefersDark = (onStoreChange: () => void) => {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
};

const getPrefersDark = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

interface CongratulationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGetCertificate?: () => void;
}

const CongratulationsModal: React.FC<CongratulationsModalProps> = ({
  isOpen,
  onClose,
  onGetCertificate,
}) => {
  const { t } = useTranslation();
  const { darkMode: darkModeFF } = useFlags();
  const isSmUp = useBreakpointUp('sm');
  const theme = useAtomValue(userPrefsAtom).theme;
  const prefersDark = useSyncExternalStore(
    subscribePrefersDark,
    getPrefersDark,
    () => false,
  );
  const mode = toDataColorMode(theme);
  const hideCongratsAnimation =
    darkModeFF && (mode === 'dark' || (mode === 'auto' && prefersDark));

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Modal.Overlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable
      className="z-2000"
    >
      <Modal.Dialog
        size="lg"
        className="w-[360px]! p-0! sm:w-[536px]!"
        isDismissable
        isCloseButtonHidden
      >
        <Modal.Content className="p-0!">
          {!hideCongratsAnimation && (
            <DotLottieReact
              src={congratsAnimationUrl}
              loop
              autoplay
              style={
                isSmUp
                  ? { height: '305px', width: '536px' }
                  : { height: '205px', width: '360px' }
              }
            />
          )}
        </Modal.Content>
        <Modal.Footer
          className={
            hideCongratsAnimation
              ? 'px-6! pt-10! pb-11! sm:px-14! sm:py-15!'
              : 'px-6! pt-0! pb-11! sm:px-14! sm:pb-15!'
          }
        >
          <div className="flex w-full flex-wrap justify-center gap-4">
            {onGetCertificate && (
              <Button
                color="secondary"
                variant="outlined"
                className="min-w-[200px]! flex-1!"
                onPress={onGetCertificate}
              >
                {t('Button.getCertificate', 'Get Certificate')}
              </Button>
            )}
            <Button
              color="secondary"
              variant="filled"
              className="min-w-[200px]! flex-1!"
              onPress={onClose}
            >
              {t('Button.close')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};

export default CongratulationsModal;
